import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// BOM subcategory types
type BOMSubcategory = 'electronic_modules' | 'structural_modules' | 'finished_products';

interface BOMDialogProps {
  onClose: () => void;
}

// Mock data for each subcategory
const MOCK_ELECTRONIC_MODULES = [
  { id: 1, part_number: 'EM-001', name: 'WiFi模組 ESP32-WROOM', spec: '802.11 b/g/n, BT 4.2', quantity: 100, unit_cost: 85 },
  { id: 2, part_number: 'EM-002', name: 'LTE模組 SIM7600G', spec: 'Cat-4 LTE, GNSS', quantity: 50, unit_cost: 450 },
  { id: 3, part_number: 'EM-003', name: '電源模組 12V-5A', spec: 'AC-DC, 效率>90%', quantity: 200, unit_cost: 120 },
  { id: 4, part_number: 'EM-004', name: 'LoRa模組 SX1278', spec: '433MHz, 10km', quantity: 75, unit_cost: 65 },
  { id: 5, part_number: 'EM-005', name: 'GPS模組 NEO-6M', spec: 'UART, 2.5m精度', quantity: 150, unit_cost: 95 },
];

const MOCK_STRUCTURAL_MODULES = [
  { id: 1, part_number: 'SM-001', name: '鋁合金外殼 A型', spec: '200x150x50mm, 陽極處理', quantity: 80, unit_cost: 250 },
  { id: 2, part_number: 'SM-002', name: '塑膠底座 B型', spec: 'ABS, 黑色, 防火V0', quantity: 120, unit_cost: 45 },
  { id: 3, part_number: 'SM-003', name: '散熱片模組', spec: '100x80x20mm, 鋁擠型', quantity: 200, unit_cost: 35 },
  { id: 4, part_number: 'SM-004', name: '防水盒組件', spec: 'IP67, PC材質', quantity: 60, unit_cost: 180 },
  { id: 5, part_number: 'SM-005', name: '機構支架套件', spec: '不銹鋼304, 含螺絲', quantity: 100, unit_cost: 75 },
];

const MOCK_FINISHED_PRODUCTS = [
  { id: 1, part_number: 'FP-001', name: '智慧電表 v2.0', spec: '單相220V, WiFi+4G', quantity: 30, unit_cost: 1500 },
  { id: 2, part_number: 'FP-002', name: '環境監測器 Pro', spec: 'PM2.5/CO2/溫濕度', quantity: 45, unit_cost: 2200 },
  { id: 3, part_number: 'FP-003', name: '車載追蹤器 GT-100', spec: 'GPS+4G, 防水IP67', quantity: 100, unit_cost: 850 },
  { id: 4, part_number: 'FP-004', name: '工業網關 IGW-500', spec: 'Modbus/MQTT, 雙網口', quantity: 25, unit_cost: 3500 },
  { id: 5, part_number: 'FP-005', name: '太陽能控制器', spec: 'MPPT, 30A, 12/24V', quantity: 50, unit_cost: 680 },
];

// Subcategory configuration
const SUBCATEGORIES = {
  electronic_modules: {
    name: '電子模組',
    icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z',
    color: 'indigo',
    data: MOCK_ELECTRONIC_MODULES,
  },
  structural_modules: {
    name: '結構模組',
    icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
    color: 'emerald',
    data: MOCK_STRUCTURAL_MODULES,
  },
  finished_products: {
    name: '成品',
    icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    color: 'amber',
    data: MOCK_FINISHED_PRODUCTS,
  },
};

const BOMDialog = ({ onClose }: BOMDialogProps) => {
  const [selectedSubcategory, setSelectedSubcategory] = useState<BOMSubcategory | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    part_number: '',
    name: '',
    spec: '',
    quantity: '',
    unit_cost: '',
  });

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; bgLight: string; text: string; border: string; hover: string }> = {
      indigo: { bg: 'bg-indigo-500', bgLight: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', hover: 'hover:bg-indigo-600' },
      emerald: { bg: 'bg-emerald-500', bgLight: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', hover: 'hover:bg-emerald-600' },
      amber: { bg: 'bg-amber-500', bgLight: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', hover: 'hover:bg-amber-600' },
    };
    return colors[color] || colors.indigo;
  };

  const handleAddItem = () => {
    // Mock add - just reset form and close
    setNewItem({ part_number: '', name: '', spec: '', quantity: '', unit_cost: '' });
    setShowAddForm(false);
  };

  const renderSubcategorySelector = () => (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">Bill of Materials</h2>
      <p className="text-center text-gray-600 dark:text-gray-400 mb-8">Select a category to view and manage items</p>

      <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto">
        {(Object.keys(SUBCATEGORIES) as BOMSubcategory[]).map((key) => {
          const sub = SUBCATEGORIES[key];
          const colors = getColorClasses(sub.color);

          return (
            <motion.button
              key={key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedSubcategory(key)}
              className={`flex flex-col items-center p-6 rounded-2xl ${colors.bgLight} ${colors.border} border-2 transition-all hover:shadow-lg`}
            >
              <div className={`w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center mb-4 shadow-md`}>
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sub.icon} />
                </svg>
              </div>
              <span className={`text-lg font-semibold ${colors.text}`}>{sub.name}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">{sub.data.length} items</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );

  const renderSubcategoryDetail = () => {
    if (!selectedSubcategory) return null;

    const sub = SUBCATEGORIES[selectedSubcategory];
    const colors = getColorClasses(sub.color);

    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className={`${colors.bg} px-6 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedSubcategory(null)}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sub.icon} />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{sub.name}</h2>
              <p className="text-white/80 text-sm">Bill of Materials</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">{sub.data.length} items</span>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white ${colors.bg} ${colors.hover} rounded-lg transition-colors`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Item
          </button>
        </div>

        {/* Add Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-b border-gray-200 dark:border-gray-700"
            >
              <div className="p-4 bg-gray-50 dark:bg-gray-700">
                <div className="grid grid-cols-5 gap-3">
                  <input
                    type="text"
                    placeholder="品號"
                    value={newItem.part_number}
                    onChange={(e) => setNewItem({ ...newItem, part_number: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    placeholder="品名"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    placeholder="規格"
                    value={newItem.spec}
                    onChange={(e) => setNewItem({ ...newItem, spec: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="number"
                    placeholder="數量"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="單價"
                      value={newItem.unit_cost}
                      onChange={(e) => setNewItem({ ...newItem, unit_cost: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                    />
                    <button
                      onClick={handleAddItem}
                      className={`px-4 py-2 ${colors.bg} ${colors.hover} text-white rounded-lg text-sm font-medium transition-colors`}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Table */}
        <div className="flex-1 overflow-auto p-4">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">品號</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">品名</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">規格</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">數量</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">單價</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">小計</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">操作</th>
              </tr>
            </thead>
            <tbody>
              {sub.data.map((item) => (
                <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 dark:bg-gray-700">
                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">{item.part_number}</td>
                  <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">{item.name}</td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{item.spec}</td>
                  <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300 text-right">{item.quantity}</td>
                  <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300 text-right">${item.unit_cost.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white text-right">
                    ${(item.quantity * item.unit_cost).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button className="p-1 hover:bg-gray-100 rounded transition-colors" title="Edit">
                        <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button className="p-1 hover:bg-red-100 rounded transition-colors" title="Delete">
                        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <td colSpan={5} className="py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 text-right">
                  Total:
                </td>
                <td className="py-3 px-4 text-sm font-bold text-gray-900 dark:text-white text-right">
                  ${sub.data.reduce((sum, item) => sum + item.quantity * item.unit_cost, 0).toLocaleString()}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {selectedSubcategory ? renderSubcategoryDetail() : (
          <>
            {renderSubcategorySelector()}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default BOMDialog;
